import React, { useState } from 'react';
import {
  Typography,
  Container,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/SEO';
import { API_CONFIG } from '../../config/apiConfig';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

const categories = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'sales', label: 'Sales & Pricing' },
  { value: 'support', label: 'Technical Support' },
  { value: 'partnership', label: 'Partnership Opportunities' },
  { value: 'press', label: 'Press & Media' },
  { value: 'other', label: 'Other' },
];

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& fieldset': { borderColor: 'rgba(77, 196, 210, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(77, 196, 210, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#4DC4D2' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(248, 248, 248, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4DC4D2' },
  '& .MuiOutlinedInput-input': { color: '#F8F8F8' },
  '& .MuiSelect-icon': { color: 'rgba(248, 248, 248, 0.6)' },
} as const;

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const isFormValid =
    formData.name.trim() &&
    formData.email.trim() &&
    formData.subject.trim() &&
    formData.message.trim();

  const handleChange = (field: keyof ContactFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_CONFIG.USER_SERVICE_URL}/api/v1/users/contact/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message. Please try again.');
      }

      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', category: 'general', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0C1319', color: '#F8F8F8' }}>
      <SEOHead
        title="Contact Us"
        description="Get in touch with the Prismatic Forge team. We'd love to hear from you about sales, support, partnerships, or general inquiries."
        canonical="https://prismaticforge.ai/contact/"
        ogUrl="https://prismaticforge.ai/contact/"
        keywords="contact Prismatic Forge, support, sales inquiry, partnership"
      />

      {/* Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 32px',
        borderBottom: '1px solid rgba(77, 196, 210, 0.1)',
      }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img src="/Final_Logo_Source_File.png" alt="Prismatic Forge" style={{ height: 32 }} />
          <Typography
            variant="h6"
            style={{ fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: 16 }}
          >
            Prismatic Forge
          </Typography>
        </div>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          style={{ color: 'rgba(248, 248, 248, 0.7)', textTransform: 'none' }}
        >
          Back to Home
        </Button>
      </div>

      <Container maxWidth="md" style={{ paddingTop: 64, paddingBottom: 64 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Typography
            variant="h2"
            style={{
              fontWeight: 800,
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              marginBottom: 16,
              background: 'linear-gradient(135deg, #4DC4D2 0%, #325BA8 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h6"
            style={{ color: 'rgba(248, 248, 248, 0.6)', fontWeight: 300, maxWidth: 600, margin: '0 auto' }}
          >
            Have a question, feedback, or want to learn more about Prismatic Forge?
            We&apos;d love to hear from you.
          </Typography>
        </div>

        {/* Contact Info Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24, marginBottom: 48 }}>
          {[
            {
              icon: <EmailIcon style={{ fontSize: 28, color: '#4DC4D2' }} />,
              label: 'Email',
              value: 'hello@prismaticforge.ai',
              href: 'mailto:hello@prismaticforge.ai',
            },
            {
              icon: <SupportIcon style={{ fontSize: 28, color: '#4DC4D2' }} />,
              label: 'Support',
              value: 'support@prismaticforge.ai',
              href: 'mailto:support@prismaticforge.ai',
            },
            {
              icon: <LocationIcon style={{ fontSize: 28, color: '#4DC4D2' }} />,
              label: 'Location',
              value: 'San Francisco, CA',
              href: undefined,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                padding: 24,
                borderRadius: 8,
                border: '1px solid rgba(77, 196, 210, 0.15)',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                textAlign: 'center',
              }}
            >
              {item.icon}
              <Typography variant="body2" style={{ color: 'rgba(248,248,248,0.5)', marginTop: 8, marginBottom: 4 }}>
                {item.label}
              </Typography>
              {item.href ? (
                <a href={item.href} style={{ color: '#F8F8F8', textDecoration: 'none' }}>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </a>
              ) : (
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {item.value}
                </Typography>
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          style={{
            padding: 32,
            borderRadius: 12,
            border: '1px solid rgba(77, 196, 210, 0.2)',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            boxShadow: '0 8px 32px rgba(77, 196, 210, 0.08)',
          }}
        >
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 24 }}>
            Send us a message
          </Typography>

          {success && (
            <Alert severity="success" onClose={() => setSuccess(false)} style={{ marginBottom: 24 }}>
              Message sent successfully! We&apos;ll get back to you soon.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')} style={{ marginBottom: 24 }}>
              {error}
            </Alert>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={formData.name}
              onChange={handleChange('name')}
              disabled={loading}
              sx={textFieldSx}
            />
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={formData.email}
              onChange={handleChange('email')}
              disabled={loading}
              sx={textFieldSx}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 16 }}>
            <TextField
              label="Category"
              select
              fullWidth
              value={formData.category}
              onChange={handleChange('category')}
              disabled={loading}
              sx={textFieldSx}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Subject"
              required
              fullWidth
              value={formData.subject}
              onChange={handleChange('subject')}
              disabled={loading}
              sx={textFieldSx}
            />
          </div>

          <TextField
            label="Message"
            required
            fullWidth
            multiline
            rows={6}
            value={formData.message}
            onChange={handleChange('message')}
            disabled={loading}
            placeholder="Tell us how we can help..."
            sx={{ ...textFieldSx, mt: 2 }}
          />

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || loading}
              endIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : success ? (
                  <CheckCircleIcon />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                background: success
                  ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                  : 'linear-gradient(135deg, #4DC4D2 0%, #325BA8 100%)',
                color: '#fff',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5dd4e2 0%, #4266b8 100%)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(77, 196, 210, 0.2)',
                  color: 'rgba(248, 248, 248, 0.3)',
                },
              }}
            >
              {loading ? 'Sending...' : success ? 'Sent!' : 'Send Message'}
            </Button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default ContactPage;
